import { useEffect } from 'react';
import { setDefaultOptions } from 'date-fns';
import Highcharts from 'highcharts';
import { useTranslation } from 'react-i18next';
import { setLocale } from 'yup';
import locale from 'yup/lib/locale';

import { MONTHS } from 'clipsal-cortex-utils/src/constants/common-constants';

import { getLocaleForDateFns, getLocaleForYup, SupportedLocales } from '../../utils/common/common-utils';
import { useRegionalConfig } from './use-regional-config';

/**
 * Patches `Pointer.prototype.onContainerClick` to guard against `this.chart` being undefined.
 * This can occur during an unmount race condition: the chart is destroyed (React component unmounts)
 * while a click event is still in the browser's event queue, causing:
 *   TypeError: undefined is not an object (evaluating 'e.hoverPoint')
 *
 * This is a known Highcharts v10.x bug fixed in v11.x.
 * Sentry issue: https://sentry.io/organizations/clipsal-solar/issues/7482785205/
 */
function patchHighchartsPointer() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Pointer = (Highcharts as any).Pointer;
  if (Pointer?.prototype?.onContainerClick) {
    const original = Pointer.prototype.onContainerClick as (e: MouseEvent) => void;
    Pointer.prototype.onContainerClick = function (e: MouseEvent) {
      if (!this.chart) return;
      original.call(this, e);
    };
  }
}

patchHighchartsPointer();

export function useHighchartSetup() {
  const { i18n, t } = useTranslation();
  const { weekStartsOn } = useRegionalConfig();

  useEffect(() => {
    // Set Highcharts language options
    // Adding this at the top level of the app to avoid having to set it in every chart component
    // See: https://api.highcharts.com/highcharts/lang
    // Highcharts is currently setting up language packs
    // https://github.com/highcharts/highcharts/pull/20474
    // Once the PR is merged, maybe we can remove this and use the language pack directly
    Highcharts.setOptions({
      lang: {
        months: MONTHS.map((month) => t(`Common.${month.toLowerCase()}`)),
        shortMonths: MONTHS.map((month) => t(`Common.${month.toLowerCase()}`).slice(0, 3)),
      },
    });

    // set locale for date-fns to use app-wide (includes weekStartsOn from regional config)
    setDefaultOptions({ locale: getLocaleForDateFns(i18n.language as SupportedLocales), weekStartsOn });

    // set locale for yup to use app-wide
    const yupLocale = getLocaleForYup(i18n.language as SupportedLocales);
    if (yupLocale) {
      setLocale(yupLocale);
    } else {
      setLocale(locale); // Resets locale to default (en-us)
    }
  }, [i18n.language, t, weekStartsOn]);
}
